import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { DateInputTR } from "@/components/DateInputTR";
import { formatDateTR, formatDateTimeTR } from "@/lib/dateTR";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  FileText, ArrowLeft, TrashSimple, Printer,
  PaperPlaneTilt, Bank, Warning, LockKey, X,
} from "@phosphor-icons/react";

const STATUS_BADGE = {
  draft:     { label: "Taslak",      cls: "bg-slate-100 text-slate-700 border-slate-300" },
  submitted: { label: "Gönderildi",  cls: "bg-amber-100 text-amber-800 border-amber-300" },
  approved:  { label: "Onaylandı",   cls: "bg-[#F7FBE9] text-[#5c8113] border-[#95C11F]" },
  rejected:  { label: "Reddedildi",  cls: "bg-red-50 text-red-700 border-red-300" },
};

const MONTH_NAMES = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran",
                     "Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];

export default function ExpenseClaimForm() {
  const { cid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user;

  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/expense-claims/id/${cid}`);
      setClaim(data);
      setForm({
        period_month: data.period_month,
        period_year: data.period_year,
        form_date: data.form_date,
        user_iban: data.user_iban || "",
        user_position: data.user_position || "",
        notes: data.notes || "",
      });
    } catch (e) { toast.error(formatErr(e)); nav("/masraf/talep-formlari"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  if (loading || !claim) {
    return <Shell hideSiteSelector title="Yükleniyor…"><div className="py-16 text-center text-sm text-slate-500">Yükleniyor…</div></Shell>;
  }

  const badge = STATUS_BADGE[claim.status] || STATUS_BADGE.draft;
  const isOwner = claim.user_id === user?.id || claim.created_by === user?.id;
  const canEdit = (isAdmin || (isOwner && ["draft", "rejected"].includes(claim.status)));
  const canDelete = (isAdmin || (isOwner && ["draft", "rejected"].includes(claim.status))) && claim.status !== "approved";
  const canSubmit = claim.status === "draft" && (isOwner || isAdmin);

  const monthLabel = MONTH_NAMES[(claim.period_month || 1) - 1] + " " + claim.period_year;

  const save = async () => {
    try {
      await axios.put(`${API}/expense-claims/id/${cid}`, form);
      toast.success("Kaydedildi"); setEditing(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submit = async () => {
    try { await axios.post(`${API}/expense-claims/id/${cid}/submit`); toast.success("Onaya gönderildi"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const removeExpense = async (eid) => {
    if (!window.confirm("Bu masrafı formdan çıkarmak istediğinize emin misiniz?")) return;
    try { await axios.post(`${API}/expense-claims/id/${cid}/remove-expense/${eid}`); toast.success("Çıkarıldı"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const removeClaim = async () => {
    if (!window.confirm(`${claim.code} formunu silmek istediğinize emin misiniz? Formdaki masraflar serbest kalır.`)) return;
    try { await axios.delete(`${API}/expense-claims/id/${cid}`); toast.success("Silindi"); nav("/masraf/talep-formlari"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const openPdf = () => window.open(`${API}/expense-claims/id/${cid}/pdf`, "_blank");

  const ibanMissing = !claim.user_iban || claim.user_iban.length < 16;

  return (
    <Shell
      title={`Masraf Talep Formu — ${claim.code}`}
      subtitle="Masraf Bildirimi › Talep Formu Detayı"
      hideSiteSelector
      actions={
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => nav("/masraf/talep-formlari")}
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
            <ArrowLeft size={14} className="mr-1.5" /> Listeye Dön
          </Button>
          <Button variant="outline" onClick={openPdf} data-testid="claim-pdf"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
            <Printer size={14} className="mr-1.5" /> Yazdır / PDF
          </Button>
          {canSubmit && (
            <Button onClick={submit} data-testid="claim-submit"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-amber-600 hover:bg-amber-700 text-white">
              <PaperPlaneTilt size={14} className="mr-1.5" /> Onaya Gönder
            </Button>
          )}
          {canDelete && (
            <Button variant="outline" onClick={removeClaim} data-testid="claim-delete"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest text-red-600 border-red-300 hover:bg-red-50">
              <TrashSimple size={14} className="mr-1.5" /> Sil
            </Button>
          )}
        </div>
      }
    >
      {/* IBAN uyarısı */}
      {ibanMissing && (
        <Card className="p-4 rounded-none sm:rounded-sm mb-3 border-l-4 border-amber-500 bg-amber-50 flex items-start gap-3">
          <Warning size={22} weight="fill" className="text-amber-500 shrink-0" />
          <div className="text-sm">
            <div className="font-bold text-amber-800">IBAN Bilgisi Eksik</div>
            <div className="text-amber-700 text-xs mt-0.5">
              Bu formda ödeme yapılabilmesi için 26 haneli IBAN gereklidir. Aşağıdan girin ve formu güncelleyin.
            </div>
          </div>
        </Card>
      )}

      {/* Üst özet */}
      <Card className="rounded-none sm:rounded-sm mb-4 overflow-hidden">
        <div className="bg-[#1B4776] text-white px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#95C11F]">Form No</div>
            <div className="text-xl font-mono font-bold">{claim.code}</div>
          </div>
          <span className={`px-3 py-1 text-[10px] uppercase tracking-widest font-bold border ${badge.cls}`}>
            {badge.label}
          </span>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 bg-white">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500">Adı Soyadı</div>
            <div className="text-sm font-bold text-slate-900" data-testid="claim-user-name">{claim.user_name}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500">Görevi</div>
            {editing ? (
              <Input value={form.user_position} onChange={e => setForm({ ...form, user_position: e.target.value })}
                     data-testid="claim-position-input" className="rounded-none sm:rounded-sm h-8 mt-1 text-sm" />
            ) : (
              <div className="text-sm font-medium text-slate-800">{claim.user_position || <span className="text-slate-400">—</span>}</div>
            )}
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500">Çalışılan Proje / Birim</div>
            <div className="text-sm font-medium text-slate-800">
              {claim.site_name || "— Genel —"}
              {claim.site_code && <span className="text-slate-400 font-mono ml-1">({claim.site_code})</span>}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500">Form Tarihi</div>
            {editing ? (
              <DateInputTR value={form.form_date} onChange={v => setForm({ ...form, form_date: v })}
                           data-testid="claim-date-input" className="h-8 mt-1 text-sm" />
            ) : (
              <div className="text-sm font-mono font-bold text-slate-900">{formatDateTR(claim.form_date)}</div>
            )}
          </div>

          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500">Ait Olduğu Dönem (Ay/Yıl)</div>
            {editing ? (
              <div className="flex gap-1 mt-1">
                <Select value={String(form.period_month)}
                        onValueChange={v => setForm({ ...form, period_month: Number(v) })}>
                  <SelectTrigger className="rounded-none sm:rounded-sm h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTH_NAMES.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" value={form.period_year}
                       onChange={e => setForm({ ...form, period_year: Number(e.target.value) })}
                       className="rounded-none sm:rounded-sm h-8 w-20 font-mono text-sm" />
              </div>
            ) : (
              <div className="text-sm font-bold text-slate-800">{monthLabel}</div>
            )}
          </div>
          <div className="md:col-span-2">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
              <Bank size={11} /> IBAN
            </div>
            {editing ? (
              <Input value={form.user_iban}
                     onChange={e => setForm({ ...form, user_iban: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 26) })}
                     data-testid="claim-iban-input" placeholder="TR330006100519786457841326" maxLength={26}
                     className="rounded-none sm:rounded-sm h-8 mt-1 font-mono text-sm" />
            ) : (
              <div className={`text-sm font-mono font-bold ${ibanMissing ? "text-red-600" : "text-slate-800"}`}>
                {claim.user_iban ? claim.user_iban.match(/.{1,4}/g).join(" ") : <span className="text-red-600">— Girilmedi —</span>}
              </div>
            )}
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500">Toplam Bedel (Brüt)</div>
            <div className="text-2xl font-mono font-bold text-[#95C11F]" data-testid="claim-total">
              ₺{(claim.total_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        {editing && (
          <div className="p-3 border-t border-slate-200 bg-slate-50">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Notlar</Label>
            <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                      className="rounded-none sm:rounded-sm mt-1" />
          </div>
        )}

        {canEdit && (
          <div className="p-3 border-t border-slate-200 flex justify-end gap-2">
            {editing ? (
              <>
                <Button variant="outline" onClick={() => { setEditing(false); load(); }}
                        className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">Vazgeç</Button>
                <Button onClick={save} data-testid="claim-save"
                        className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-[#1B4776] text-white">Kaydet</Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setEditing(true)} data-testid="claim-edit"
                      className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">Düzenle</Button>
            )}
          </div>
        )}
      </Card>

      {/* Reddedildi ise gerekçe + kilit açık bilgisi */}
      {claim.status === "rejected" && (
        <Card className="p-3 rounded-none sm:rounded-sm mb-3 border-l-4 border-red-400 bg-red-50">
          <div className="text-[10px] uppercase tracking-widest font-bold text-red-700 mb-1">Red Gerekçesi</div>
          <div className="text-sm text-red-800 mb-2">{claim.rejection_reason || "-"}</div>
          <div className="text-[11px] text-red-700 italic border-t border-red-200 pt-2 mt-2 flex items-center gap-1">
            <LockKey size={12} />
            <span>Bu form reddedildiği için içindeki masrafların <b>kilidi kaldırıldı</b>. Yeniden form oluşturmak için Masraf Listesi&apos;ne dönün.</span>
          </div>
        </Card>
      )}
      {claim.status === "approved" && claim.approved_by_name && (
        <Card className="p-3 rounded-none sm:rounded-sm mb-3 border-l-4 border-[#95C11F] bg-[#F7FBE9] flex items-center gap-2">
          <LockKey size={16} weight="duotone" className="text-[#5c8113]" />
          <div className="text-xs text-[#5c8113]">
            <b>{claim.approved_by_name}</b> tarafından onaylandı
            {claim.approved_at && <span> · {formatDateTimeTR(claim.approved_at)}</span>}
            <span className="ml-2 italic">— tüm masraflar kilitlendi</span>
          </div>
        </Card>
      )}

      {/* Masraf kalemleri */}
      <Card className="rounded-none sm:rounded-sm overflow-hidden">
        <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <FileText size={16} weight="duotone" className="text-[#1B4776]" />
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700">
            Masraf Kalemleri ({claim.expense_count})
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600 bg-slate-100">
              <tr>
                <th className="text-left px-3 py-2">Tarih</th>
                <th className="text-left px-3 py-2">Kategori</th>
                <th className="text-left px-3 py-2">Firma / Açıklama</th>
                <th className="text-right px-3 py-2">Brüt</th>
                <th className="text-right px-3 py-2">KDV</th>
                <th className="text-right px-3 py-2">Net</th>
                {canEdit && claim.status !== "approved" && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {(claim.expenses || []).map((e, i) => (
                <tr key={e.id} className="border-t border-slate-100" data-testid={`claim-item-${i}`}>
                  <td className="px-3 py-2 font-mono text-xs">{formatDateTR(e.date)}</td>
                  <td className="px-3 py-2 text-xs">{e.category_label}</td>
                  <td className="px-3 py-2 text-xs">
                    {e.vendor_name && <div className="font-medium">{e.vendor_name}</div>}
                    <div className="text-slate-500">{e.description || "-"}</div>
                    {e.vendor_tax_number && <div className="text-[9px] font-mono text-slate-400">VKN {e.vendor_tax_number}</div>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-[#1B4776]">
                    ₺{(e.amount_gross || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-amber-700">
                    ₺{(e.vat_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    ₺{(e.net_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                  </td>
                  {canEdit && claim.status !== "approved" && (
                    <td className="text-center">
                      <Button size="sm" variant="ghost" onClick={() => removeExpense(e.id)}
                              data-testid={`claim-item-remove-${i}`}
                              title="Formdan çıkar"
                              className="h-7 px-2 text-red-500 hover:bg-red-50">
                        <X size={14} />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                <td colSpan={3} className="px-3 py-2 text-right text-[10px] uppercase tracking-widest">Toplam</td>
                <td className="px-3 py-2 text-right font-mono text-[#1B4776]">
                  ₺{(claim.total_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                </td>
                <td className="px-3 py-2 text-right font-mono text-amber-700">
                  ₺{(claim.vat_total || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  ₺{(claim.net_total || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                </td>
                {canEdit && claim.status !== "approved" && <td></td>}
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {claim.notes && !editing && (
        <Card className="p-3 rounded-none sm:rounded-sm mt-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Notlar</div>
          <div className="text-sm text-slate-700 mt-1">{claim.notes}</div>
        </Card>
      )}
    </Shell>
  );
}
